/**
 * RCD — Penalty Waiver Wizard.
 *
 * Split authority per `memory/cd-penalty-waiver-v3.md` (Collette 2026-04-20):
 *   | Reason Code         | Authority                         |
 *   | DEATH               | Officer, Supervisor, Manager      |
 *   | NURSING_HOME        | Officer, Supervisor, Manager      |
 *   | COLLATERAL_RELEASE  | Officer (loan-side; was 'LoanOfficer' before role-schema rework 2026-04-27) |
 *   | OTHER               | Manager approval via RAP          |
 *
 * Source of truth is the SQL table `CdPenaltyWaiverReasons` (see
 * `scripts/seed/08-cd-penalty-rules.sql` + `sql-target-schema.md` §5.7.6).
 * We now read the typed `config.cdPenaltyWaiverReasons` rows off ConfigBundle
 * (wired in `ConfigContext` against `/v3/config/cd-penalty-waiver-reasons`).
 * The in-app `FALLBACK_REASONS` only kicks in while the route 503s (server
 * not yet seeded) — its authority mapping mirrors the seed row for row.
 *
 * On submit:
 *   1. Ledger the waiver (fire SessionEvent `cd-waiver-requested` + downstream
 *      writes handled by Function `/v3/cd-waivers` — planned Wave 3).
 *   2. Navigate back to CD detail.
 */
import { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { ArrowLeft, CheckCircle2, AlertTriangle } from '@retail-ops/panel-shell/fi';
import { Button, Pill, WidgetCard, KVPair } from '@/components/primitives';
import { useApiClient } from '@/contexts/ApiClientContext';
import { useAuth } from '@/contexts/AuthContext';
import { useConfig } from '@/contexts/ConfigContext';
import type { UserRole } from '@retail-ops/shared-types/enums';
import type { CdPenaltyWaiverReason } from '@retail-ops/shared-types';
import { useCdDetail } from './hooks/useCdDetail';
import { formatMoney } from './utils/cdFormat';
import './rcd.css';

type WaiverReason = 'DEATH' | 'NURSING_HOME' | 'COLLATERAL_RELEASE' | 'OTHER';

interface ReasonDef {
  code: WaiverReason;
  label: string;
  description: string;
  allowedRoles: ReadonlyArray<UserRole>;
  requiresApproval: boolean;
  routesToRap: boolean;
}

/**
 * FALLBACK_REASONS — mirrors `scripts/seed/08-cd-penalty-rules.sql` row for row.
 *
 * Each row's `allowedRoles` is derived from the seed `RequiredRoleCode` column:
 *   - seed `Officer` for DEATH/NURSING_HOME → FSR + Supervisor + Manager
 *     (per Collette, FSR can waive without supervisor sign-off).
 *   - seed `Officer` (loan-officer sense) for COLLATERAL_RELEASE → `LoanOfficer`
 *     only (UserRole union gained this member in Wave 3).
 *   - seed `Manager` with RequiresApproval=1 → Manager + `routesToRap: true`.
 *
 * Only used as a fallback while `/v3/config/cd-penalty-waiver-reasons` 503s.
 */
const FALLBACK_REASONS: ReadonlyArray<ReasonDef> = [
  {
    code: 'DEATH',
    label: 'Death of an owner',
    description: 'Customer death on or before maturity. Supporting documentation in RCC.',
    allowedRoles: ['Officer', 'Supervisor', 'Manager'],
    requiresApproval: false,
    routesToRap: false,
  },
  {
    code: 'NURSING_HOME',
    label: 'Nursing home care',
    description: 'Per Collette: an Officer can waive when funds are being used for nursing home care.',
    allowedRoles: ['Officer', 'Supervisor', 'Manager'],
    requiresApproval: false,
    routesToRap: false,
  },
  {
    code: 'COLLATERAL_RELEASE',
    label: 'CD collateral — loan being paid off',
    description:
      'Loan Officer authority: waive when the secured loan is being paid off.',
    allowedRoles: ['Officer'],
    requiresApproval: false,
    routesToRap: false,
  },
  {
    code: 'OTHER',
    label: 'Other reason (justification required)',
    description:
      'Requires free-text justification and Manager approval via RAP (Retail Approval Panel).',
    allowedRoles: ['Manager'],
    requiresApproval: true,
    routesToRap: true,
  },
];

/**
 * Map a `RequiredRoleCode` from SQL → the shared-types `UserRole` union.
 *
 * The SQL seed uses the Roles-table code (e.g. `Officer`, `LoanOfficer`,
 * `Manager`, `Supervisor`, `FSR`). Per Collette's memory the generic
 * `Officer` code in the DEATH/NURSING_HOME rows is shorthand for
 * "any FSR/Supervisor/Manager can waive"; the COLLATERAL_RELEASE row is
 * specifically LoanOfficer. Anything unmapped falls back to `['Manager']`
 * so the waiver still has an approver.
 */
function rolesForSeedCode(code: string): ReadonlyArray<UserRole> {
  switch (code) {
    // Legacy 'FSR' + 'LoanOfficer' seed codes both collapse to the unified
    // 'Officer' role (post role-schema rework 2026-04-27).
    case 'Officer':
    case 'FSR':
    case 'LoanOfficer':
      return ['Officer', 'Supervisor', 'Manager'];
    case 'Supervisor':
      return ['Supervisor', 'Manager'];
    case 'Manager':
      return ['Manager'];
    case 'SuperAdmin':
      return ['SuperAdmin'];
    default:
      return ['Manager'];
  }
}

function toReasonDef(row: CdPenaltyWaiverReason): ReasonDef {
  return {
    code: row.reasonCode as WaiverReason,
    label: row.displayName,
    description: row.description ?? '',
    allowedRoles: rolesForSeedCode(row.requiredRoleCode),
    requiresApproval: row.requiresApproval,
    routesToRap: row.requiresApproval,
  };
}

export function PenaltyWaiverWizard(): JSX.Element {
  const { acctId } = useParams<{ acctId: string }>();
  const navigate = useNavigate();
  const apiClient = useApiClient();
  const { user } = useAuth();
  const { config } = useConfig();

  const [selected, setSelected] = useState<WaiverReason | null>(null);
  const [justification, setJustification] = useState<string>('');
  const [submitState, setSubmitState] = useState<'idle' | 'submitting' | 'submitted' | 'error'>('idle');
  const [submitError, setSubmitError] = useState<string | null>(null);

  // Real CD detail from the AcctInq dual-call (see `services/CdInqService.ts`).
  // `cdDetail.penaltyAmount` is straight from `x_TimeDepInfoRec.PenAmt` —
  // Core Director computes the penalty, officers never do math.
  const { data: cdDetail, loading: calcLoading, error: calcError } = useCdDetail(acctId);
  const penaltyAmount = cdDetail?.penaltyAmount ?? null;

  // Hydrate the canonical reason list.
  //
  // Source of truth: SQL `CdPenaltyWaiverReasons` (seeded per
  // memory/cd-penalty-waiver-v3.md). Server route:
  // `GET /v3/config/cd-penalty-waiver-reasons` — populated into
  // `config.cdPenaltyWaiverReasons` by ConfigContext. Before v3.0 ETL
  // seeds SQL (route 503s), ConfigContext leaves that array empty and we
  // use `FALLBACK_REASONS` which mirrors the seed row-for-row.
  const reasons = useMemo<ReadonlyArray<ReasonDef>>(() => {
    const seeded = config.cdPenaltyWaiverReasons;
    if (seeded && seeded.length > 0) {
      return seeded.filter((r) => r.isActive).map(toReasonDef);
    }
    return FALLBACK_REASONS;
  }, [config.cdPenaltyWaiverReasons]);

  // Penalty calc: read straight from the merged `cdDetail.penaltyAmount` (live
  // `x_TimeDepInfoRec.PenAmt` from Core Director). Per
  // `memory/cd-penalty-waiver-v3.md`: "Core Director has an interest
  // calculator within the CD that computes the penalty automatically.
  // Officer doesn't do math." The old bespoke AcctInq call in this file
  // was replaced by `useCdDetail` (2026-04-22) so every RCD surface
  // hydrates from the same dual-call service (see `CdInqService.ts`).

  const currentRole: UserRole | null = user?.primaryRole ?? null;
  const chosen = reasons.find((r) => r.code === selected) ?? null;

  const authorityOk: boolean = useMemo(() => {
    if (!chosen || !currentRole) return false;
    return chosen.allowedRoles.includes(currentRole);
  }, [chosen, currentRole]);

  const canSubmit = (): boolean => {
    if (!chosen) return false;
    if (!authorityOk) return false;
    if (chosen.code === 'OTHER' && justification.trim().length < 10) return false;
    return true;
  };

  const submit = async (): Promise<void> => {
    if (!chosen || !user || !acctId) return;
    setSubmitState('submitting');
    setSubmitError(null);

    try {
      await apiClient.retail.events.write({
        eventType: chosen.routesToRap ? 'cd-waiver-requested' : 'cd-waiver-approved',
        eventCategory: 'cif-account',
        severity: 'warn',
        actor: user.upn,
        payloadJson: JSON.stringify({
          acctId,
          acctType: '30',
          reasonCode: chosen.code,
          reasonLabel: chosen.label,
          justification: chosen.code === 'OTHER' ? justification : null,
          penaltyAmount,
          penaltyFormulaCode: cdDetail?.penaltyFormulaCode ?? null,
          balance: cdDetail?.balance ?? null,
          currentRate: cdDetail?.currentRate ?? null,
          maturityDate: cdDetail?.maturityDate ?? null,
          termMonths: cdDetail?.termMonths ?? null,
          authority: currentRole,
          routedToRap: chosen.routesToRap,
        }),
      });
      setSubmitState('submitted');
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : 'Failed to record waiver.');
      setSubmitState('error');
    }
  };

  return (
    <div className="rcd-wizard">
      <div className="rcd-detail__crumb">
        <Button variant="ghost" startIcon={<ArrowLeft size={14} />} onClick={() => navigate('..')}>
          Back to CD
        </Button>
        <h1>Waive early-withdrawal penalty · {acctId}</h1>
      </div>

      <WidgetCard title="Penalty calculation (Core Director)" stripe="compliance">
        <div className="rcd-detail__kvgrid">
          <KVPair label="CD account" value={acctId ?? '—'} />
          <KVPair
            label="Penalty amount"
            value={
              calcLoading
                ? 'Calculating…'
                : penaltyAmount == null
                  ? '—'
                  : formatMoney(penaltyAmount)
            }
          />
          <KVPair
            label="Current balance"
            value={cdDetail?.balance == null ? '—' : formatMoney(cdDetail.balance)}
          />
          <KVPair
            label="Current rate"
            value={cdDetail?.currentRate == null ? '—' : `${cdDetail.currentRate.toFixed(2)}%`}
          />
          <KVPair label="Calc source" value="Core Director interest calculator" />
        </div>
        {calcError && (
          <div className="rcd-banner rcd-banner--err" role="alert">
            Unable to load CD detail from Core Director — {calcError}. Penalty amount
            must be confirmed manually before submitting.
          </div>
        )}
      </WidgetCard>

      <WidgetCard title="Reason for waiver" stripe="approvals">
        <div className="rcd-wizard__reason-grid">
          {reasons.map((r) => {
            const isSelected = selected === r.code;
            const roleOk = currentRole ? r.allowedRoles.includes(currentRole) : false;
            return (
              <label key={r.code} className={`card-radio ${isSelected ? 'is-selected' : ''}`}>
                <input
                  type="radio"
                  name="rcd-waiver-reason"
                  value={r.code}
                  checked={isSelected}
                  onChange={() => setSelected(r.code)}
                  disabled={!roleOk}
                />
                <div className="card-radio__body">
                  <span className="card-radio__primary">
                    {r.label}{' '}
                    {!roleOk && (
                      <Pill variant="muted">Requires {r.allowedRoles.join(' / ')}</Pill>
                    )}
                    {r.routesToRap && <Pill variant="warn">Routes to RAP</Pill>}
                  </span>
                  <span className="card-radio__helper">{r.description}</span>
                </div>
              </label>
            );
          })}
        </div>
      </WidgetCard>

      {chosen?.code === 'OTHER' && (
        <WidgetCard title="Justification (required for OTHER)" stripe="compliance">
          <div className="rcd-wizard__field">
            <label htmlFor="rcd-waiver-just">Justification</label>
            <textarea
              id="rcd-waiver-just"
              value={justification}
              onChange={(e) => setJustification(e.target.value)}
              placeholder="Describe the business reason for the waiver. Must be ≥10 characters."
            />
          </div>
        </WidgetCard>
      )}

      {chosen && !authorityOk && currentRole && (
        <div className="rcd-wizard__gate">
          <AlertTriangle size={14} /> Your role ({currentRole}) is not authorized to waive{' '}
          <strong>{chosen.label}</strong>. Allowed roles: {chosen.allowedRoles.join(', ')}.
        </div>
      )}

      {chosen && authorityOk && (
        <div className="rcd-wizard__gate rcd-wizard__gate--ok">
          <CheckCircle2 size={14} /> Authority confirmed — {currentRole} may waive{' '}
          <strong>{chosen.label}</strong>.
        </div>
      )}

      {submitError && (
        <div className="rcd-banner rcd-banner--err" role="alert">
          {submitError}
        </div>
      )}

      {submitState === 'submitted' && (
        <div className="rcd-banner rcd-banner--ok" role="status">
          <CheckCircle2 size={16} className="rcd-inline-icon" />
          <div>
            Waiver recorded. A CD Redemption v25.0 PDF has been queued for customer e-sign (RSC).
            You will see it in the signing center momentarily.
          </div>
        </div>
      )}

      <div className="rcd-wizard__footer">
        <Button variant="ghost" onClick={() => navigate('..')}>
          Cancel
        </Button>
        <Button
          variant="primary"
          disabled={!canSubmit() || submitState === 'submitting'}
          onClick={() => void submit()}
        >
          {submitState === 'submitting' ? 'Recording…' : 'Record waiver & generate form'}
        </Button>
      </div>
    </div>
  );
}
